import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  CheckCircle2,
  FileSignature,
  Hand,
  Loader2,
  LockKeyhole,
  MousePointerClick,
  Move,
  PenLine,
  Type,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { API_BASE } from '../utils/api';

const PDFJS_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174';

let pdfJsLoaderPromise = null;

const loadPdfJs = () => {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (!pdfJsLoaderPromise) {
    pdfJsLoaderPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `${PDFJS_CDN}/pdf.min.js`;
      script.async = true;
      script.onload = () => {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS_CDN}/pdf.worker.min.js`;
        resolve(window.pdfjsLib);
      };
      script.onerror = () => reject(new Error('Failed to load PDF viewer'));
      document.head.appendChild(script);
    });
  }
  return pdfJsLoaderPromise;
};

const SignaturePad = ({ overlayId, onDrawn, className, initialImage }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const onDrawnRef = useRef(onDrawn);
  const restoredRef = useRef(false);
  onDrawnRef.current = onDrawn;

  const restoreImage = useCallback((dataUrl) => {
    const canvas = canvasRef.current;
    if (!canvas || !dataUrl) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      canvas.width = Math.round(rect.width);
      canvas.height = Math.round(rect.height);
    }
    const img = new Image();
    img.onload = () => {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    };
    img.src = dataUrl;
  }, []);

  useEffect(() => {
    restoredRef.current = false;
  }, [overlayId]);

  useEffect(() => {
    if (initialImage && !restoredRef.current) {
      restoredRef.current = true;
      restoreImage(initialImage);
    }
  }, [initialImage, restoreImage]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const notify = () => {
      if (onDrawnRef.current) onDrawnRef.current(overlayId, canvas.toDataURL('image/png'));
    };
    const start = (e) => {
      drawing.current = true;
      const ctx = canvas.getContext('2d');
      const rect = canvas.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0 && (canvas.width !== Math.round(rect.width) || canvas.height !== Math.round(rect.height))) {
        const snapshot = canvas.toDataURL('image/png');
        canvas.width = Math.round(rect.width);
        canvas.height = Math.round(rect.height);
        if (snapshot && snapshot !== 'data:,') restoreImage(snapshot);
      }
      const x = (e.clientX ?? e.touches?.[0]?.clientX) - rect.left;
      const y = (e.clientY ?? e.touches?.[0]?.clientY) - rect.top;
      ctx.strokeStyle = '#111';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(x, y);
    };
    const move = (e) => {
      if (!drawing.current) return;
      e.preventDefault();
      const ctx = canvas.getContext('2d');
      const rect = canvas.getBoundingClientRect();
      const x = (e.clientX ?? e.touches?.[0]?.clientX) - rect.left;
      const y = (e.clientY ?? e.touches?.[0]?.clientY) - rect.top;
      ctx.lineTo(x, y);
      ctx.stroke();
    };
    const stop = () => {
      if (drawing.current) notify();
      drawing.current = false;
    };
    canvas.addEventListener('mousedown', start);
    canvas.addEventListener('mousemove', move);
    canvas.addEventListener('mouseup', stop);
    canvas.addEventListener('mouseleave', stop);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', stop);
    return () => {
      canvas.removeEventListener('mousedown', start);
      canvas.removeEventListener('mousemove', move);
      canvas.removeEventListener('mouseup', stop);
      canvas.removeEventListener('mouseleave', stop);
      canvas.removeEventListener('touchstart', start);
      canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', stop);
    };
  }, [overlayId, restoreImage]);

  return <canvas ref={canvasRef} className={className} />;
};

const SuccessPanel = ({ title, message, completedUrl, signedAt }) => (
  <div className="flex flex-col items-center justify-center py-16 px-6 text-center max-w-lg mx-auto">
    <CheckCircle2 className="w-16 h-16 text-green-500 mb-4" />
    <h1 className="text-2xl font-bold text-[var(--color-text)] mb-2">{title}</h1>
    <p className="text-[var(--color-text-muted)] mb-2">{message}</p>
    {signedAt && (
      <p className="text-sm text-[var(--color-text-muted)] mb-6">Signed on {new Date(signedAt).toLocaleString()}</p>
    )}
    {completedUrl && (
      <a
        href={completedUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[var(--color-primary)] text-[var(--color-text-inverse)] font-medium hover:bg-[var(--color-primary-hover)]"
      >
        Download completed document
      </a>
    )}
    <Link to="/" className="mt-6 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
      Go to DocxsAi home
    </Link>
  </div>
);

const SignPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [session, setSession] = useState(null);
  const [accessCode, setAccessCode] = useState('');
  const [accessVerified, setAccessVerified] = useState(false);
  const [fieldValues, setFieldValues] = useState({});
  const [annotations, setAnnotations] = useState([]);
  const [consent, setConsent] = useState(false);
  const [done, setDone] = useState(false);
  const [alreadySigned, setAlreadySigned] = useState(false);
  const [completedUrl, setCompletedUrl] = useState(null);
  const [signedAt, setSignedAt] = useState(null);

  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfZoom, setPdfZoom] = useState(1);
  const [pageRenderSize, setPageRenderSize] = useState({ width: 0, height: 0 });
  const [toolMode, setToolMode] = useState('scroll');
  const [addMode, setAddMode] = useState(false);
  const [pendingText, setPendingText] = useState('');
  const [selectedAnnotationId, setSelectedAnnotationId] = useState(null);

  const scrollRef = useRef(null);
  const pageWrapRef = useRef(null);
  const canvasRef = useRef(null);
  const dragRef = useRef(null);
  const annotationSigRef = useRef({});
  const fieldSignaturesRef = useRef({});

  const pageCount = pdfDoc?.numPages || session?.page_count || 1;
  const assignedFields = session?.fields || [];
  const pageAssignedFields = assignedFields.filter((f) => f.page === currentPage);
  const pageAnnotations = annotations.filter((a) => a.page === currentPage);

  const updateAnnotation = useCallback((id, patch) => {
    setAnnotations((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/esign/sign/${token}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || 'Invalid signing link');

        if (data.already_signed) {
          setAlreadySigned(true);
          setCompletedUrl(data.completed_pdf_url || null);
          setSignedAt(data.signed_at || null);
          setSession(data);
          return;
        }

        setSession(data);
        const initial = {};
        (data.fields || []).forEach((f) => {
          if (f.field_type === 'date') initial[f.id] = new Date().toISOString().slice(0, 10);
          if (f.field_type === 'name') initial[f.id] = data.signer?.name || '';
          if (f.field_type === 'email') initial[f.id] = data.signer?.email || '';
        });
        setFieldValues(initial);
      } catch (err) {
        toast.error(err.message || 'Could not load signing session');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  useEffect(() => {
    if (!accessVerified || !session?.pdf_url) {
      setPdfDoc(null);
      return undefined;
    }
    let cancelled = false;
    setPdfLoading(true);
    (async () => {
      try {
        const pdfjsLib = await loadPdfJs();
        const res = await fetch(session.pdf_url);
        if (!res.ok) throw new Error('Failed to fetch PDF');
        const data = await res.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data }).promise;
        if (!cancelled) setPdfDoc(pdf);
      } catch (err) {
        if (!cancelled) toast.error(err.message || 'Failed to load document');
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessVerified, session?.pdf_url]);

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const page = await pdfDoc.getPage(currentPage);
        const baseViewport = page.getViewport({ scale: 1 });
        const containerWidth = scrollRef.current?.clientWidth || baseViewport.width;
        const fitScale = Math.min(1.5, Math.max(0.5, (containerWidth - 32) / baseViewport.width));
        const scale = fitScale * pdfZoom;
        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: context, viewport }).promise;
        if (!cancelled) setPageRenderSize({ width: viewport.width, height: viewport.height });
      } catch (err) {
        if (!cancelled) toast.error(err.message || 'Failed to render page');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfDoc, currentPage, pdfZoom]);

  useEffect(() => {
    const handleMove = (event) => {
      if (!dragRef.current || !pageWrapRef.current) return;
      const rect = pageWrapRef.current.getBoundingClientRect();
      const {
        id,
        mode,
        startX,
        startY,
        origX,
        origY,
        origW,
        origH,
        widthPct,
        heightPct,
      } = dragRef.current;

      if (mode === 'resize') {
        const dw = ((event.clientX - startX) / rect.width) * 100;
        const dh = ((event.clientY - startY) / rect.height) * 100;
        updateAnnotation(id, {
          width_pct: Math.max(4, Math.min(100 - origX, origW + dw)),
          height_pct: Math.max(2, Math.min(100 - origY, origH + dh)),
        });
        return;
      }

      const dx = ((event.clientX - startX) / rect.width) * 100;
      const dy = ((event.clientY - startY) / rect.height) * 100;
      updateAnnotation(id, {
        x_pct: Math.max(0, Math.min(100 - widthPct, origX + dx)),
        y_pct: Math.max(0, Math.min(100 - heightPct, origY + dy)),
      });
    };
    const handleUp = () => {
      dragRef.current = null;
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [updateAnnotation]);

  const verifyAccess = async () => {
    if (!accessCode.trim() || accessCode.trim().length < 4) {
      toast.error('Enter the access code provided by the sender');
      return;
    }
    setVerifying(true);
    try {
      const res = await fetch(`${API_BASE}/esign/sign/${token}/verify-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ access_code: accessCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Invalid access code');
      setAccessVerified(true);
      toast.success(`Welcome, ${data.signer_name || session?.signer?.name || 'signer'}`);
    } catch (err) {
      toast.error(err.message || 'Invalid access code');
    } finally {
      setVerifying(false);
    }
  };

  const startMove = (event, item) => {
    event.stopPropagation();
    event.preventDefault();
    setSelectedAnnotationId(item.id);
    dragRef.current = {
      id: item.id,
      mode: 'move',
      startX: event.clientX,
      startY: event.clientY,
      origX: item.x_pct,
      origY: item.y_pct,
      widthPct: item.width_pct,
      heightPct: item.height_pct,
    };
  };

  const startResize = (event, item) => {
    event.stopPropagation();
    event.preventDefault();
    setSelectedAnnotationId(item.id);
    dragRef.current = {
      id: item.id,
      mode: 'resize',
      startX: event.clientX,
      startY: event.clientY,
      origX: item.x_pct,
      origY: item.y_pct,
      origW: item.width_pct,
      origH: item.height_pct,
      widthPct: item.width_pct,
      heightPct: item.height_pct,
    };
  };

  const placeOnPage = (event) => {
    if (!addMode || toolMode === 'scroll' || !pageWrapRef.current) return;
    event.stopPropagation();
    const rect = pageWrapRef.current.getBoundingClientRect();
    const xPct = ((event.clientX - rect.left) / rect.width) * 100;
    const yPct = ((event.clientY - rect.top) / rect.height) * 100;

    if (toolMode === 'addText') {
      const newId = Date.now();
      setAnnotations((prev) => [
        ...prev,
        {
          id: newId,
          type: 'text',
          page: currentPage,
          x_pct: Math.max(0, Math.min(88, xPct - 8)),
          y_pct: Math.max(0, Math.min(94, yPct - 2)),
          width_pct: 18,
          height_pct: 3.5,
          text: pendingText,
          font_size: 11,
        },
      ]);
      setSelectedAnnotationId(newId);
      setPendingText('');
      setAddMode(false);
      setToolMode('scroll');
      return;
    }

    if (toolMode === 'addSignature') {
      const newId = Date.now() + 1;
      setAnnotations((prev) => [
        ...prev,
        {
          id: newId,
          type: 'signature',
          page: currentPage,
          x_pct: Math.max(0, Math.min(76, xPct - 10)),
          y_pct: Math.max(0, Math.min(90, yPct - 3)),
          width_pct: 20,
          height_pct: 7,
          imageData: null,
        },
      ]);
      setSelectedAnnotationId(newId);
      setAddMode(false);
      setToolMode('scroll');
    }
  };

  const removeAnnotation = (id) => {
    setAnnotations((prev) => prev.filter((a) => a.id !== id));
    delete annotationSigRef.current[id];
    if (selectedAnnotationId === id) setSelectedAnnotationId(null);
  };

  const selectedAnnotation = annotations.find((a) => a.id === selectedAnnotationId) || null;

  const handleAnnotationSignature = useCallback((id, dataUrl) => {
    annotationSigRef.current[id] = dataUrl;
  }, []);

  const handleFieldSignature = useCallback((fieldId, dataUrl) => {
    fieldSignaturesRef.current[String(fieldId)] = dataUrl;
  }, []);

  const submit = async () => {
    if (!consent) {
      toast.error('Please agree to sign electronically');
      return;
    }
    if (!accessVerified || !accessCode.trim()) {
      toast.error('Verify your access code before signing');
      return;
    }

    const payloadAnnotations = annotations.map((a) => ({
      page: a.page,
      x_pct: a.x_pct,
      y_pct: a.y_pct,
      width_pct: a.width_pct,
      height_pct: a.height_pct,
      annotation_type: a.type === 'signature' ? 'signature' : 'text',
      value: a.type === 'text' ? a.text : undefined,
      image_data: a.type === 'signature' ? annotationSigRef.current[a.id] : undefined,
      font_size: a.font_size || 11,
    }));

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/esign/sign/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          consent: true,
          access_code: accessCode.trim(),
          field_values: Object.fromEntries(
            Object.entries(fieldValues).map(([k, v]) => [String(k), v])
          ),
          field_signatures: fieldSignaturesRef.current,
          annotations: payloadAnnotations,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Signing failed');
      setDone(true);
      setCompletedUrl(data.completed_pdf_url || null);
      setSignedAt(new Date().toISOString());
      toast.success('Document signed successfully');
    } catch (err) {
      toast.error(err.message || 'Signing failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24 text-[var(--color-text-muted)]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!session && !alreadySigned) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
        <FileSignature className="w-12 h-12 text-gray-600 mb-4" />
        <h1 className="text-xl text-[var(--color-text)] mb-2">Signing link unavailable</h1>
        <p className="text-[var(--color-text-muted)] mb-6">This link may have expired or been voided.</p>
        <Link to="/" className="text-[var(--color-primary)] hover:text-[var(--color-primary)]">
          Go to DocxsAi
        </Link>
      </div>
    );
  }

  if (alreadySigned || done) {
    return (
      <SuccessPanel
        title={alreadySigned ? 'Already signed' : 'Thank you!'}
        message={
          alreadySigned
            ? 'You have already signed this document. No further action is needed.'
            : 'Your signature has been recorded.'
        }
        completedUrl={completedUrl}
        signedAt={signedAt || session?.signed_at}
      />
    );
  }

  const hasAssignedFields = assignedFields.length > 0;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div>
        <p className="text-sm text-[var(--color-primary)] font-medium mb-1">DocxsAi eSign</p>
        <h1 className="text-2xl font-bold text-[var(--color-text)]">{session.request_title}</h1>
        {session.message && <p className="text-[var(--color-text-muted)] mt-2">{session.message}</p>}
        <p className="text-sm text-[var(--color-text-muted)] mt-1">Signing as {session.signer?.name}</p>
      </div>

      {!accessVerified ? (
        <div className="p-6 rounded-xl bg-[var(--color-surface)] border border-[var(--color-border)] max-w-md">
          <div className="flex items-center gap-2 mb-3">
            <LockKeyhole className="w-5 h-5 text-[var(--color-primary)]" />
            <h2 className="text-lg font-semibold text-[var(--color-text)]">Enter access code</h2>
          </div>
          <p className="text-sm text-[var(--color-text-muted)] mb-4">
            This document is protected. Enter the access code from your signing invitation to continue.
          </p>
          <label className="block text-sm text-[var(--color-text-muted)] mb-1">Access code</label>
          <input
            value={accessCode}
            onChange={(e) => setAccessCode(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && verifyAccess()}
            placeholder="Required — from your email"
            className="w-full rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text)] px-3 py-2 mb-4"
          />
          <button
            type="button"
            disabled={verifying}
            onClick={verifyAccess}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg brand-gradient-btn font-medium disabled:opacity-60"
          >
            {verifying && <Loader2 className="w-4 h-4 animate-spin" />}
            Verify &amp; continue
          </button>
        </div>
      ) : (
        <>
          {!hasAssignedFields && (
            <div className="p-4 rounded-lg bg-[var(--color-primary-soft)] border border-[var(--color-primary)]/40 text-sm text-[var(--color-primary)]">
              This document has printed signature lines. Use <strong>Add text</strong> and{' '}
              <strong>Add signature</strong> below, then click on the document where each value should appear.
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="px-3 py-1 rounded bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] disabled:opacity-40 text-sm"
            >
              Prev
            </button>
            <span className="text-[var(--color-text-muted)] text-sm">
              Page {currentPage} / {pageCount}
            </span>
            <button
              type="button"
              disabled={currentPage >= pageCount}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="px-3 py-1 rounded bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] disabled:opacity-40 text-sm"
            >
              Next
            </button>
            <span className="text-gray-600 hidden sm:inline">|</span>
            <button
              type="button"
              onClick={() => {
                setToolMode('scroll');
                setAddMode(false);
              }}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm ${
                toolMode === 'scroll' && !addMode ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)]' : 'bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)]'
              }`}
            >
              <Hand className="w-4 h-4" /> Scroll
            </button>
            <button
              type="button"
              onClick={() => {
                setToolMode('select');
                setAddMode(false);
              }}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm ${
                toolMode === 'select' ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)]' : 'bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)]'
              }`}
            >
              <Move className="w-4 h-4" /> Move / resize
            </button>
            <button
              type="button"
              onClick={() => {
                setToolMode('addText');
                setAddMode(true);
              }}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm ${
                toolMode === 'addText' && addMode ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)]' : 'bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)]'
              }`}
            >
              <Type className="w-4 h-4" /> Add text
            </button>
            <button
              type="button"
              onClick={() => {
                setToolMode('addSignature');
                setAddMode(true);
              }}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm ${
                toolMode === 'addSignature' && addMode ? 'bg-[var(--color-primary)] text-[var(--color-text-inverse)]' : 'bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)]'
              }`}
            >
              <PenLine className="w-4 h-4" /> Add signature
            </button>
            <button
              type="button"
              onClick={() => {
                setPendingText(session.signer?.name || '');
                setToolMode('addText');
                setAddMode(true);
              }}
              className="px-3 py-1.5 rounded-lg text-sm bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)]"
            >
              + My name
            </button>
            <button
              type="button"
              onClick={() => {
                setPendingText(new Date().toISOString().slice(0, 10));
                setToolMode('addText');
                setAddMode(true);
              }}
              className="px-3 py-1.5 rounded-lg text-sm bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-muted)]"
            >
              + Today&apos;s date
            </button>
            <button type="button" onClick={() => setPdfZoom((z) => Math.max(0.6, z - 0.1))} className="p-1.5 rounded bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)]">
              <ZoomOut className="w-4 h-4" />
            </button>
            <span className="text-xs text-[var(--color-text-muted)] w-10 text-center">{Math.round(pdfZoom * 100)}%</span>
            <button type="button" onClick={() => setPdfZoom((z) => Math.min(1.8, z + 0.1))} className="p-1.5 rounded bg-[var(--color-surface-muted)] text-[var(--color-text-secondary)]">
              <ZoomIn className="w-4 h-4" />
            </button>
          </div>

          {selectedAnnotation && (
            <div className="p-3 rounded-lg bg-[var(--color-surface)] border border-[var(--color-border)] grid sm:grid-cols-4 gap-3 max-w-3xl">
              <p className="sm:col-span-4 text-sm text-[var(--color-text)] capitalize">
                Selected {selectedAnnotation.type} on page {selectedAnnotation.page}
              </p>
              <div>
                <label className="text-xs text-[var(--color-text-muted)]">Width ({Math.round(selectedAnnotation.width_pct)}%)</label>
                <input
                  type="range"
                  min="4"
                  max="60"
                  value={selectedAnnotation.width_pct}
                  onChange={(e) =>
                    updateAnnotation(selectedAnnotation.id, { width_pct: Number(e.target.value) })
                  }
                  className="w-full"
                />
              </div>
              <div>
                <label className="text-xs text-[var(--color-text-muted)]">Height ({Math.round(selectedAnnotation.height_pct)}%)</label>
                <input
                  type="range"
                  min="2"
                  max="25"
                  value={selectedAnnotation.height_pct}
                  onChange={(e) =>
                    updateAnnotation(selectedAnnotation.id, { height_pct: Number(e.target.value) })
                  }
                  className="w-full"
                />
              </div>
              {selectedAnnotation.type === 'text' && (
                <div>
                  <label className="text-xs text-[var(--color-text-muted)]">
                    Font size ({selectedAnnotation.font_size || 11}px)
                  </label>
                  <input
                    type="range"
                    min="8"
                    max="24"
                    value={selectedAnnotation.font_size || 11}
                    onChange={(e) =>
                      updateAnnotation(selectedAnnotation.id, { font_size: Number(e.target.value) })
                    }
                    className="w-full"
                  />
                </div>
              )}
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => removeAnnotation(selectedAnnotation.id)}
                  className="text-sm text-red-400 hover:text-red-300"
                >
                  Remove
                </button>
              </div>
            </div>
          )}

          {addMode && toolMode !== 'scroll' && toolMode !== 'select' && (
            <p className="text-xs text-[var(--color-primary)] flex items-center gap-1">
              <MousePointerClick className="w-3.5 h-3.5" />
              Click on the document to place {toolMode === 'addSignature' ? 'a signature box' : 'text'}
              {pendingText ? ` (“${pendingText}”)` : ''}
            </p>
          )}

          <div
            ref={scrollRef}
            className={`w-full h-[70vh] min-h-[480px] bg-[var(--color-surface)] rounded-lg overflow-auto border border-[var(--color-border)] ${
              addMode ? 'cursor-crosshair' : ''
            }`}
          >
            <div className="flex justify-center p-4 min-h-full">
              {pdfLoading && (
                <div className="flex items-center justify-center text-[var(--color-text-muted)] py-24">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              )}
              {!pdfLoading && session.pdf_url && (
                <div
                  ref={pageWrapRef}
                  data-page-bg="true"
                  className="relative bg-white shadow-lg shrink-0"
                  style={{ width: pageRenderSize.width || '100%', height: pageRenderSize.height || 'auto' }}
                  onClick={placeOnPage}
                >
                  <canvas ref={canvasRef} className="block w-full h-auto" />

                  {pageAssignedFields.map((field) => (
                    <div
                      key={field.id}
                      className="absolute border border-blue-400/70 bg-transparent overflow-hidden"
                      style={{
                        left: `${field.x_pct}%`,
                        top: `${field.y_pct}%`,
                        width: `${field.width_pct}%`,
                        height: `${field.height_pct}%`,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {field.field_type === 'signature' || field.field_type === 'initial' ? (
                        <SignaturePad
                          overlayId={field.id}
                          onDrawn={handleFieldSignature}
                          initialImage={fieldSignaturesRef.current[String(field.id)]}
                          className="w-full h-full cursor-crosshair touch-none bg-transparent"
                        />
                      ) : field.field_type === 'checkbox' ? (
                        <div className="flex items-center justify-center h-full">
                          <input
                            type="checkbox"
                            checked={fieldValues[field.id] === 'true'}
                            onChange={(e) =>
                              setFieldValues({ ...fieldValues, [field.id]: e.target.checked ? 'true' : 'false' })
                            }
                            className="w-4 h-4"
                          />
                        </div>
                      ) : (
                        <input
                          value={fieldValues[field.id] || ''}
                          onChange={(e) => setFieldValues({ ...fieldValues, [field.id]: e.target.value })}
                          className="w-full h-full px-1 text-sm text-gray-900 bg-transparent border-0 outline-none"
                          placeholder={field.label || field.field_type}
                        />
                      )}
                    </div>
                  ))}

                  {pageAnnotations.map((ann) => {
                    const isSelected = selectedAnnotationId === ann.id;
                    return (
                      <div
                        key={ann.id}
                        className={`absolute group ${
                          isSelected
                            ? 'border-2 border-[var(--color-primary)] ring-2 ring-[var(--color-ring)]'
                            : 'border border-[var(--color-primary)]/60 hover:border-[var(--color-primary)]'
                        } bg-transparent`}
                        style={{
                          left: `${ann.x_pct}%`,
                          top: `${ann.y_pct}%`,
                          width: `${ann.width_pct}%`,
                          height: `${ann.height_pct}%`,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedAnnotationId(ann.id);
                        }}
                      >
                        <div
                          role="button"
                          tabIndex={0}
                          title="Drag to move"
                          onMouseDown={(e) => startMove(e, ann)}
                          className={`absolute -top-2 left-0 right-0 h-2.5 bg-[var(--color-primary)]/50 cursor-move rounded-t ${
                            isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeAnnotation(ann.id);
                          }}
                          className="absolute -top-2 -right-2 z-10 hidden group-hover:flex w-5 h-5 items-center justify-center rounded-full bg-red-500 text-[var(--color-text)] text-xs"
                          title="Remove"
                        >
                          ×
                        </button>
                        {ann.type === 'signature' ? (
                          <SignaturePad
                            overlayId={ann.id}
                            onDrawn={handleAnnotationSignature}
                            initialImage={annotationSigRef.current[ann.id]}
                            className="w-full h-full cursor-crosshair touch-none bg-transparent"
                          />
                        ) : (
                          <input
                            value={ann.text || ''}
                            onChange={(e) => updateAnnotation(ann.id, { text: e.target.value })}
                            onMouseDown={(e) => e.stopPropagation()}
                            className="w-full h-full px-0.5 text-gray-900 bg-transparent border-0 outline-none placeholder:text-[var(--color-text-muted)]/70"
                            placeholder="Type here"
                            style={{ fontSize: `${ann.font_size || 11}px` }}
                          />
                        )}
                        <div
                          role="button"
                          tabIndex={0}
                          title="Drag to resize"
                          onMouseDown={(e) => startResize(e, ann)}
                          className={`absolute bottom-0 right-0 w-3.5 h-3.5 bg-[var(--color-primary)] cursor-se-resize rounded-sm ${
                            isSelected ? 'opacity-100' : 'opacity-60 group-hover:opacity-100'
                          }`}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <p className="text-xs text-[var(--color-text-muted)]">
            Click a box to select it. Drag the top edge to move, the bottom-right corner to resize, or use the sliders
            above. Boxes are transparent so you can align with printed lines.
          </p>

          <label className="flex items-start gap-2 text-sm text-[var(--color-text-muted)]">
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1" />
            <span>
              I agree to sign this document electronically. I understand that my electronic signature is the legal
              equivalent of my manual signature.
            </span>
          </label>

          <button
            type="button"
            disabled={submitting}
            onClick={submit}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)] font-medium disabled:opacity-60"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            Complete signing
          </button>
        </>
      )}
    </div>
  );
};

export default SignPage;
